import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Container, 
    Paper, 
    Grid, 
    Stack, 
    Button,
    Chip,
    CircularProgress,
    alpha,
    useTheme,
    Snackbar,
    Alert,
    Avatar
} from '@mui/material';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import careService from '../services/careService';
import { CareStaffAssignmentDTO } from '../types/crm.types';

const StaffAssignmentPage: React.FC = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [assignments, setAssignments] = useState<CareStaffAssignmentDTO[]>([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });

    useEffect(() => {
        const fetchAssignments = async () => {
            try {
                const data = await careService.getAssignments();
                setAssignments(data);
            } catch (error) {
                console.error('Failed to fetch assignments', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAssignments();
    }, []);

    const handleAction = (msg: string) => {
        setSnackbar({ open: true, message: msg });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'SCHEDULED': return theme.palette.info.main;
            case 'ON_CALL': return theme.palette.warning.main;
            case 'COMPLETED': return theme.palette.success.main;
            case 'CANCELLED': return theme.palette.error.main;
            default: return theme.palette.grey[500];
        }
    };

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 4 }}>
                <Box>
                    <Typography variant="h4" fontWeight="800" color="text.primary">
                        Staff Assignments
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage care staff schedules, resident visits, and facility tasks.
                    </Typography>
                </Box>
                <Button 
                    variant="contained" 
                    startIcon={<AssignmentIndIcon />} 
                    onClick={() => handleAction('Assignment board coming soon')}
                    sx={{ borderRadius: 2, fontWeight: '700' }}
                >
                    Assign Staff
                </Button>
            </Box>

            <Grid container spacing={4}>
                <Grid item xs={12} lg={8}>
                    <Stack spacing={3}>
                        {loading ? (
                            <Box display="flex" justifyContent="center" py={5}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            assignments.map((assignment) => (
                                <Paper 
                                    key={assignment.id} 
                                    elevation={0} 
                                    sx={{ 
                                        p: 3, 
                                        border: '1px solid', 
                                        borderColor: 'divider', 
                                        borderRadius: 4,
                                        '&:hover': { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.01) },
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                                        <Box>
                                            <Chip 
                                                label={assignment.status} 
                                                size="small" 
                                                sx={{ 
                                                    mb: 1, 
                                                    fontWeight: '800', 
                                                    color: '#fff', 
                                                    bgcolor: getStatusColor(assignment.status),
                                                    borderRadius: 1.5
                                                }} 
                                            />
                                            <Typography variant="h6" fontWeight="800">{assignment.residentName}</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <LocationOnIcon fontSize="inherit" /> {assignment.unitLabel}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 700 }}>
                                                {assignment.staffName[0]}
                                            </Avatar>
                                            <Typography variant="subtitle2" fontWeight="700" color="primary">
                                                {assignment.staffName}
                                            </Typography>
                                        </Stack>
                                    </Box>

                                    <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                                        "{assignment.taskDescription}"
                                    </Typography>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <AccessTimeIcon fontSize="small" color="action" />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" display="block">Scheduled Time</Typography>
                                                    <Typography variant="body2" fontWeight="700">
                                                        {new Date(assignment.scheduledAt).toLocaleString()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <CalendarMonthIcon fontSize="small" color="action" />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" display="block">Task Priority</Typography>
                                                    <Typography variant="body2" fontWeight="700">Routine Care</Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                    
                                    <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 3 }}>
                                        <Button size="small" variant="text" onClick={() => handleAction('Viewing care logs...')}>View Logs</Button>
                                        <Button size="small" variant="outlined" sx={{ borderRadius: 1.5 }} onClick={() => handleAction('Modification dialog coming soon')}>Reassign Task</Button>
                                    </Box>
                                </Paper>
                            ))
                        )}
                        {!loading && assignments.length === 0 && (
                            <Typography variant="body2" color="text.disabled" textAlign="center" py={5}>
                                No active care assignments found.
                            </Typography>
                        )}
                    </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Stack spacing={3}>
                        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 4 }}>
                            <Typography variant="h6" fontWeight="700" gutterBottom>Care Staff Status</Typography>
                            <Stack spacing={2}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" fontWeight="600">Nurse Jackie</Typography>
                                    <Chip label="On-Shift" size="small" color="success" sx={{ fontWeight: 700 }} />
                                </Box>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" fontWeight="600">Staff Smith</Typography>
                                    <Chip label="Assigned" size="small" color="info" sx={{ fontWeight: 700 }} />
                                </Box>
                            </Stack>
                        </Paper>
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                p: 3, 
                                bgcolor: alpha(theme.palette.secondary.main, 0.05), 
                                border: '1px dashed', 
                                borderColor: 'secondary.main', 
                                borderRadius: 4,
                                textAlign: 'center'
                            }}
                        >
                            <AssignmentIndIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
                            <Typography variant="subtitle2" fontWeight="700">Observation Reports</Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                                Daily clinical observation logs and incident reports.
                            </Typography>
                            <Button variant="outlined" color="secondary" size="small" fullWidth onClick={() => handleAction('Opening clinical reports...')}>
                                Open Observations
                            </Button>
                        </Paper>
                    </Stack>
                </Grid>
            </Grid>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={3000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default StaffAssignmentPage;
